import type {LinkingOptions} from '@react-navigation/native';
import type {RootStackParamList} from '@navigation/types';
import NAVIGATORS from '@src/NAVIGATORS';
import ROUTES from '@src/ROUTES';
import type {Screen} from '@src/SCREENS';
import SCREENS from '@src/SCREENS';
import type {RouteConfig} from './createNormalizedConfigs';
import createNormalizedConfigs from './createNormalizedConfigs';

// Moved to a separate file to avoid cyclic dependencies.
const config: LinkingOptions<RootStackParamList>['config'] = {
  initialRouteName: NAVIGATORS.BOTTOM_TAB_NAVIGATOR,
  screens: {
    // Main Routes
    [SCREENS.INITIAL]: ROUTES.INITIAL,
    [SCREENS.AUTH]: ROUTES.AUTH,
    [SCREENS.FORGOT_PASSWORD]: ROUTES.FORGOT_PASSWORD,
    [SCREENS.FORCE_UPDATE]: ROUTES.FORCE_UPDATE,
    // Root level on purpose: the same path must resolve in the public stack
    // (signed-out visitors) and the signed-in stack.
    [SCREENS.ADD_FRIEND]: ROUTES.ADD_FRIEND.route,

    // Sidebar
    [NAVIGATORS.BOTTOM_TAB_NAVIGATOR]: {
      path: ROUTES.ROOT,
      initialRouteName: SCREENS.HOME,
      screens: {
        [SCREENS.HOME]: ROUTES.HOME,
        [SCREENS.SOCIAL.ROOT]: ROUTES.SOCIAL,
        [SCREENS.STATISTICS.ROOT]: ROUTES.STATISTICS,
        [SCREENS.SETTINGS.ROOT]: {
          path: ROUTES.SETTINGS,
          exact: true,
        },
      },
    },

    [SCREENS.NOT_FOUND]: '*',
    [NAVIGATORS.LEFT_MODAL_NAVIGATOR]: {
      screens: {
        //         [SCREENS.LEFT_MODAL.SEARCH]: {
        //             screens: {
        //                 [SCREENS.SEARCH_ROOT]: ROUTES.SEARCH,
        //             },
        //         },
        // ...
      },
    },

    [NAVIGATORS.ONBOARDING_MODAL_NAVIGATOR]: {
      path: ROUTES.ONBOARDING_TERMS,
      initialRouteName: SCREENS.ONBOARDING.TERMS,
      screens: {
        [SCREENS.ONBOARDING.TERMS]: {
          path: ROUTES.ONBOARDING_TERMS,
          exact: true,
        },
        [SCREENS.ONBOARDING.DISPLAY_NAME]: {
          path: ROUTES.ONBOARDING_DISPLAY_NAME,
          exact: true,
        },
      },
    },
    [NAVIGATORS.RIGHT_MODAL_NAVIGATOR]: {
      screens: {
        [SCREENS.RIGHT_MODAL.BADGES]: {
          screens: {
            [SCREENS.BADGES.ROOT]: ROUTES.BADGES,
          },
        },
        [SCREENS.RIGHT_MODAL.DRINKING_SESSION]: {
          screens: {
            [SCREENS.DRINKING_SESSION.ROOT]: {
              path: ROUTES.DRINKING_SESSION.route,
            },
            [SCREENS.DRINKING_SESSION.LIVE]: {
              path: ROUTES.DRINKING_SESSION_LIVE.route,
            },
            [SCREENS.DRINKING_SESSION.EDIT]: {
              path: ROUTES.DRINKING_SESSION_EDIT.route,
            },
            [SCREENS.DRINKING_SESSION.SESSION_DATE_SCREEN]: {
              path: ROUTES.DRINKING_SESSION_SESSION_DATE_SCREEN.route,
            },
            [SCREENS.DRINKING_SESSION.SESSION_NAME_SCREEN]: {
              path: ROUTES.DRINKING_SESSION_SESSION_NAME_SCREEN.route,
            },
            [SCREENS.DRINKING_SESSION.SESSION_NOTE_SCREEN]: {
              path: ROUTES.DRINKING_SESSION_SESSION_NOTE_SCREEN.route,
            },
            [SCREENS.DRINKING_SESSION.SESSION_TIMEZONE_SCREEN]: {
              path: ROUTES.DRINKING_SESSION_SESSION_TIMEZONE_SCREEN.route,
            },
            [SCREENS.DRINKING_SESSION.SUMMARY]: {
              path: ROUTES.DRINKING_SESSION_SUMMARY.route,
            },
          },
        },
        [SCREENS.RIGHT_MODAL.SETTINGS]: {
          screens: {
            [SCREENS.SETTINGS.ACCOUNT.ROOT]: {
              path: ROUTES.SETTINGS_ACCOUNT,
              exact: true,
            },
            [SCREENS.SETTINGS.PREFERENCES.ROOT]: {
              path: ROUTES.SETTINGS_PREFERENCES,
              exact: true,
            },
            [SCREENS.SETTINGS.PREFERENCES.LANGUAGE]: {
              path: ROUTES.SETTINGS_LANGUAGE,
              exact: true,
            },
            [SCREENS.SETTINGS.PREFERENCES.THEME]: {
              path: ROUTES.SETTINGS_THEME,
              exact: true,
            },
            [SCREENS.SETTINGS.PREFERENCES.FIRST_DAY_OF_WEEK]: {
              path: ROUTES.SETTINGS_FIRST_DAY_OF_WEEK,
              exact: true,
            },
            [SCREENS.SETTINGS.PREFERENCES.UNITS_TO_COLORS]: {
              path: ROUTES.SETTINGS_UNITS_TO_COLORS,
              exact: true,
            },
            [SCREENS.SETTINGS.PREFERENCES.DRINKS_TO_UNITS]: {
              path: ROUTES.SETTINGS_DRINKS_TO_UNITS,
              exact: true,
            },
            [SCREENS.SETTINGS.PREFERENCES.COLOR_PALETTE]: {
              path: ROUTES.SETTINGS_COLOR_PALETTE,
              exact: true,
            },
            [SCREENS.SETTINGS.PREFERENCES.COLOR_PALETTE_CUSTOM]: {
              path: ROUTES.SETTINGS_COLOR_PALETTE_CUSTOM,
              exact: true,
            },
            [SCREENS.SETTINGS.NOTIFICATIONS]: {
              path: ROUTES.SETTINGS_NOTIFICATIONS,
              exact: true,
            },
            [SCREENS.SETTINGS.PRIVACY.ROOT]: {
              path: ROUTES.SETTINGS_PRIVACY,
              exact: true,
            },
            [SCREENS.SETTINGS.PRIVACY.BLOCKED_USERS]: {
              path: ROUTES.SETTINGS_PRIVACY_BLOCKED_USERS,
              exact: true,
            },
            [SCREENS.SETTINGS.ACCOUNT.USER_NAME]: {
              path: ROUTES.SETTINGS_USER_NAME,
              exact: true,
            },
            [SCREENS.SETTINGS.ACCOUNT.DISPLAY_NAME]: {
              path: ROUTES.SETTINGS_DISPLAY_NAME,
              exact: true,
            },
            [SCREENS.SETTINGS.ACCOUNT.EMAIL]: {
              path: ROUTES.SETTINGS_EMAIL,
              exact: true,
            },
            [SCREENS.SETTINGS.ACCOUNT.PASSWORD]: {
              path: ROUTES.SETTINGS_PASSWORD,
              exact: true,
            },
            [SCREENS.SETTINGS.ACCOUNT.CONNECTED_ACCOUNTS]: {
              path: ROUTES.SETTINGS_CONNECTED_ACCOUNTS,
              exact: true,
            },
            [SCREENS.SETTINGS.ACCOUNT.TIMEZONE]: {
              path: ROUTES.SETTINGS_TIMEZONE,
              exact: true,
            },
            [SCREENS.SETTINGS.ACCOUNT.TIMEZONE_SELECT]: {
              path: ROUTES.SETTINGS_TIMEZONE_SELECT,
              exact: true,
            },
            [SCREENS.SETTINGS.ADMIN.ROOT]: {
              path: ROUTES.SETTINGS_ADMIN,
              exact: true,
            },
            [SCREENS.SETTINGS.ADMIN.FEEDBACK]: {
              path: ROUTES.SETTINGS_ADMIN_FEEDBACK,
              exact: true,
            },
            [SCREENS.SETTINGS.ADMIN.BUGS]: {
              path: ROUTES.SETTINGS_ADMIN_BUGS,
              exact: true,
            },
            [SCREENS.SETTINGS.APP_SHARE]: ROUTES.SETTINGS_APP_SHARE,
            [SCREENS.SETTINGS.SUPPORT]: ROUTES.SETTINGS_SUPPORT,
            [SCREENS.SETTINGS.HELP]: ROUTES.SETTINGS_HELP,
            [SCREENS.SETTINGS.MANAGE_SUBSCRIPTION]: {
              path: ROUTES.SETTINGS_MANAGE_SUBSCRIPTION,
              exact: true,
            },
            [SCREENS.SETTINGS.PRIVACY_POLICY]: ROUTES.SETTINGS_PRIVACY_POLICY,
            [SCREENS.SETTINGS.TERMS_OF_SERVICE]:
              ROUTES.SETTINGS_TERMS_OF_SERVICE,
            [SCREENS.SETTINGS.SUBSCRIPTION_TERMS]:
              ROUTES.SETTINGS_SUBSCRIPTION_TERMS,
            [SCREENS.SETTINGS.REPORT_BUG]: ROUTES.SETTINGS_REPORT_BUG,
            [SCREENS.SETTINGS.FEEDBACK]: ROUTES.SETTINGS_FEEDBACK,
            [SCREENS.SETTINGS.ABOUT]: ROUTES.SETTINGS_ABOUT,
            [SCREENS.SETTINGS.MANAGE_ACCOUNT]: {
              path: ROUTES.SETTINGS_MANAGE_ACCOUNT,
              exact: true,
            },
            [SCREENS.SETTINGS.DELETE]: {
              path: ROUTES.SETTINGS_DELETE,
              exact: true,
            },
          },
        },
        [SCREENS.RIGHT_MODAL.PROFILE]: {
          screens: {
            [SCREENS.PROFILE.ROOT]: {
              path: ROUTES.PROFILE.route,
            },
            [SCREENS.PROFILE.FRIENDS_FRIENDS]: {
              path: ROUTES.PROFILE_FRIENDS_FRIENDS.route,
            },
            [SCREENS.PROFILE.REPORT_USER]: {
              path: ROUTES.PROFILE_REPORT_USER.route,
            },
          },
        },
        [SCREENS.RIGHT_MODAL.SOCIAL]: {
          screens: {
            [SCREENS.SOCIAL.FRIEND_LIST]: ROUTES.SOCIAL_FRIEND_LIST,
            [SCREENS.SOCIAL.FRIEND_REQUESTS]: ROUTES.SOCIAL_FRIEND_REQUESTS,
            [SCREENS.SOCIAL.ADD_FRIENDS]: ROUTES.SOCIAL_ADD_FRIENDS.route,
          },
        },
      },
    },

    [NAVIGATORS.SESSIONS_CALENDAR_NAVIGATOR]: {
      screens: {
        [SCREENS.SESSIONS_CALENDAR.FULLSCREEN]: {
          path: ROUTES.SESSIONS_CALENDAR_FULLSCREEN.route,
        },
      },
    },

    [NAVIGATORS.DAY_OVERVIEW_NAVIGATOR]: {
      screens: {
        [SCREENS.DAY_OVERVIEW.ROOT]: {
          path: ROUTES.DAY_OVERVIEW.route,
        },
      },
    },
  },
};

const normalizedConfigs = Object.keys(config.screens)
  .map(key =>
    createNormalizedConfigs(
      key,
      config.screens,
      [],
      config.initialRouteName
        ? [
            {
              initialRouteName: config.initialRouteName,
              parentScreens: [],
            },
          ]
        : [],
      [],
    ),
  )
  .flat()
  .reduce(
    (acc, route) => {
      acc[route.screen as Screen] = route;
      return acc;
    },
    {} as Record<Screen, RouteConfig>,
  );
export {normalizedConfigs};
export default config;
